<!-- Project Deletion 2-Step Wizard Dialog -->
<div class="delete-dialog-container">
  <!-- Header (Always Visible) -->
  <div mat-dialog-title class="delete-dialog-header">
    <div class="header-content">
      <mat-icon class="warning-icon">warning</mat-icon>
      <div class="title-section">
        <h2 class="delete-title">Delete Project Permanently</h2>
        <p class="project-id-subtitle">Project ID: {{ data.projectId }}</p>
      </div>
    </div>
  </div>

  <!-- Dialog Content -->
  <div mat-dialog-content class="delete-dialog-content">
    <!-- Step 1: Warning Phase -->
    @if (isWarningStep()) {
      <div class="warning-step">
        <mat-card class="warning-card">
          <mat-card-content>
            <div class="warning-content">
              <div class="warning-text">
                <h3>⚠️ IRREVERSIBLE OPERATION - This operation cannot be undone</h3>
                <p>You will permanently lose:</p>
                <ul class="loss-list">
                  <li>All project files and documents</li>
                  <li>All AI analysis and compliance reports</li>
                  <li>All project metadata and history</li>
                  <li>Any funds spent on AI analysis</li>
                </ul>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    }

    <!-- Step 2: Confirmation Phase -->
    @if (isConfirmationStep()) {
      <div class="confirmation-step">
        <!-- Safety Confirmation Section -->
        <div class="confirmation-section">
          <h3 class="confirmation-title">
            <mat-icon class="security-icon">security</mat-icon>
            Safety Confirmation Required
          </h3>
          <!-- Delete Text Confirmation -->
          <mat-form-field class="confirmation-field" appearance="fill">
            <mat-label>Type "DELETE" to confirm</mat-label>
            <input
              matInput
              [(ngModel)]="confirmationDeleteText"
              (ngModelChange)="onDeleteTextChange()"
              placeholder="DELETE"
              autocomplete="off"
              class="confirmation-input"
            />
            <mat-icon matSuffix class="input-icon">edit</mat-icon>
            @if (deleteTextFormControl.invalid) {
              <mat-error>
                {{ getDeleteTextErrorMessage() }}
              </mat-error>
            }
          </mat-form-field>
          <!-- Project ID Confirmation -->
          <mat-form-field class="confirmation-field" appearance="fill">
            <mat-label>Type the project ID to confirm</mat-label>
            <input
              matInput
              [(ngModel)]="confirmationProjectId"
              (ngModelChange)="onProjectIdChange()"
              [placeholder]="data.projectId"
              autocomplete="off"
              class="confirmation-input"
            />
            <mat-icon matSuffix class="input-icon">badge</mat-icon>
            @if (projectIdFormControl.invalid) {
              <mat-error>
                {{ getProjectIdErrorMessage() }}
              </mat-error>
            }
          </mat-form-field>
          <!-- Validation Status -->
          @if (confirmationDeleteText || confirmationProjectId) {
            <div class="validation-status">
              <div
                class="validation-item"
                [class.valid]="deleteTextFormControl.valid"
                [class.invalid]="deleteTextFormControl.invalid && deleteTextFormControl.touched"
              >
                <mat-icon>{{ deleteTextFormControl.valid ? 'check_circle' : 'cancel' }}</mat-icon>
                <span>DELETE confirmation</span>
              </div>
              <div
                class="validation-item"
                [class.valid]="projectIdFormControl.valid"
                [class.invalid]="projectIdFormControl.invalid && projectIdFormControl.touched"
              >
                <mat-icon>{{ projectIdFormControl.valid ? 'check_circle' : 'cancel' }}</mat-icon>
                <span>Project ID confirmation</span>
              </div>
            </div>
          }
        </div>
        <!-- Deletion Options (Expandable) -->
        @if (shouldShowHardDeleteOption()) {
          <mat-expansion-panel class="deletion-options-panel" [(expanded)]="deletionOptionsExpanded">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon>tune</mat-icon>
                <span>Deletion Options</span>
              </mat-panel-title>
              <mat-panel-description>
                {{ deletionMode === 'hard' ? 'Hard Delete Selected' : 'Soft Delete (Default)' }}
              </mat-panel-description>
            </mat-expansion-panel-header>
            <mat-radio-group [(ngModel)]="deletionMode" class="deletion-mode-group">
              <div class="deletion-option">
                <mat-radio-button value="soft" class="deletion-radio">
                  <span class="deletion-label">
                    Soft Delete (recommended)
                    <mat-icon
                      class="info-icon"
                      matTooltip="Namespace locked for 7-90 days (recoverable)"
                      matTooltipPosition="right"
                      matTooltipClass="deletion-tooltip"
                    >
                      info
                    </mat-icon>
                  </span>
                </mat-radio-button>
              </div>
              <div class="deletion-option">
                <mat-radio-button value="hard" class="deletion-radio">
                  <span class="deletion-label">
                    Hard Delete (no-restore)
                    <mat-icon
                      class="info-icon warning"
                      matTooltip="Namespace immediately available for reuse (permanent)"
                      matTooltipPosition="right"
                      matTooltipClass="deletion-tooltip warning-tooltip"
                    >
                      warning
                    </mat-icon>
                  </span>
                </mat-radio-button>
              </div>
            </mat-radio-group>
          </mat-expansion-panel>
        }
        <!-- Error Display -->
        @if (error) {
          <div class="error-section">
            <mat-card class="error-card">
              <mat-card-content>
                <div class="error-content">
                  <mat-icon class="error-icon">error</mat-icon>
                  <span class="error-message">{{ error }}</span>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        }
        <!-- Loading Indicator -->
        @if (loading) {
          <div class="loading-section">
            <mat-progress-bar mode="indeterminate" class="delete-progress"></mat-progress-bar>
            <p class="loading-text">Deleting project... This may take a moment.</p>
          </div>
        }
      </div>
    }
  </div>

  <!-- Dialog Actions (Dynamic based on step) -->
  <div mat-dialog-actions class="delete-dialog-actions">
    <!-- Step 1 Actions: Cancel + Continue -->
    @if (isWarningStep()) {
      <div class="step-actions">
        <button mat-button (click)="onCancel()" class="cancel-button">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button color="primary" (click)="onContinue()" class="continue-button">
          <mat-icon>arrow_forward</mat-icon>
          Continue
        </button>
      </div>
    }

    <!-- Step 2 Actions: Back + Cancel + Delete -->
    @if (isConfirmationStep()) {
      <div class="step-actions">
        <button mat-button (click)="onBack()" [disabled]="loading" class="back-button">
          <mat-icon>arrow_back</mat-icon>
          Back
        </button>
        <button mat-button (click)="onCancel()" [disabled]="loading" class="cancel-button">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button color="warn" (click)="onDelete()" [disabled]="!canDelete()" class="delete-button">
          <mat-icon>delete_forever</mat-icon>
          {{ getDeleteButtonText() }}
        </button>
      </div>
    }
  </div>
</div>
